import { useCallback, useMemo, useState } from 'react';
import { Button, Card, Divider, Form, Input, Modal, Select, Spin, Table, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAddRiskCard, useRiskCards, useUpdateRiskCard } from '@/entities/risk/api/hooks';
import { antTablePagination, antTableScroll } from '@/shared/constants/antTable';
import type { RiskCard } from '@/entities/risk/model/riskSchema';
import { probabilitySeverityOptions, riskCardStatusOptions } from '../constants';
import { getRiskCardColumns } from '../riskCardColumns';
import { riskLevelFromScore } from '../utils';
import s from './CardsTab.module.scss';

const { Text } = Typography;

type CardsTabProps = {
  /** Из карточки сотрудника — показать карты рисков по рабочему месту. */
  workplaceIdFilter?: string;
};

export function CardsTab({ workplaceIdFilter }: CardsTabProps) {
  const { data: cards, isLoading: cardsLoading } = useRiskCards();
  const addCard = useAddRiskCard();
  const updateCard = useUpdateRiskCard();
  const navigate = useNavigate();

  const [cardModal, setCardModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingCard, setEditingCard] = useState<RiskCard | null>(null);

  const [cardForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const pWatch = Form.useWatch('probability', cardForm);
  const sWatch = Form.useWatch('severity', cardForm);
  const previewScore = typeof pWatch === 'number' && typeof sWatch === 'number' ? pWatch * sWatch : null;

  const editPWatch = Form.useWatch('probability', editForm);
  const editSWatch = Form.useWatch('severity', editForm);
  const editPreviewScore =
    typeof editPWatch === 'number' && typeof editSWatch === 'number' ? editPWatch * editSWatch : null;

  const allCards = useMemo(() => {
    const list = cards ?? [];
    if (!workplaceIdFilter) return list;
    return list.filter((c) => c.workplaceId === workplaceIdFilter);
  }, [cards, workplaceIdFilter]);

  const openEdit = useCallback(
    (record: RiskCard) => {
      setEditingCard(record);
      editForm.setFieldsValue({
        probability: record.probability,
        severity: record.severity,
        justification: record.justification ?? '',
        source: record.source ?? '',
        reviewDate: record.reviewDate ?? '',
        status: record.status,
      });
      setEditModal(true);
    },
    [editForm],
  );

  const riskColumns = useMemo(() => getRiskCardColumns(openEdit), [openEdit]);

  const submitCard = async () => {
    const v = await cardForm.validateFields();
    const riskScore = v.probability * v.severity;
    const workplaceId = v.workplace.trim().split(/\s+/)[0] || v.workplace;
    const hasRequired = v.justification?.trim() && v.source?.trim();
    addCard.mutate(
      {
        workplaceId,
        workplace: v.workplace,
        hazard: v.hazard,
        probability: v.probability,
        severity: v.severity,
        level: riskLevelFromScore(riskScore),
        riskScore,
        justification: v.justification,
        source: v.source,
        reviewDate: v.reviewDate || undefined,
        status: hasRequired ? 'info' : 'warn',
      },
      {
        onSuccess: () => {
          setCardModal(false);
          message.success(
            hasRequired
              ? 'Карта риска создана (статус «Согласование»)'
              : 'Карта сохранена как черновик — заполните обоснование и источник для утверждения',
          );
        },
      },
    );
  };

  const submitEdit = async () => {
    if (!editingCard) return;
    const v = await editForm.validateFields();
    const riskScore = v.probability * v.severity;
    const hasRequired = v.justification?.trim() && v.source?.trim();
    updateCard.mutate(
      {
        id: editingCard.id,
        probability: v.probability,
        severity: v.severity,
        level: riskLevelFromScore(riskScore),
        riskScore,
        justification: v.justification,
        source: v.source,
        reviewDate: v.reviewDate || undefined,
        status: v.status === 'ok' && !hasRequired ? 'info' : v.status,
      },
      {
        onSuccess: () => {
          setEditModal(false);
          setEditingCard(null);
          message.success('Карта обновлена, расчёт пересчитан');
        },
      },
    );
  };

  return (
    <>
      <Card
        size="small"
        title={<span className={s['root__card-title']}>Карты рисков</span>}
        extra={
          <Button
            type="primary"
            onClick={() => {
              cardForm.resetFields();
              setCardModal(true);
            }}
          >
            + Новая карта
          </Button>
        }
      >
        <Spin spinning={cardsLoading}>
          <Table
            dataSource={allCards}
            columns={riskColumns}
            rowKey="id"
            size="small"
            pagination={{ ...antTablePagination }}
            scroll={antTableScroll}
            onRow={(record) => ({
              onClick: (e) => {
                const target = e.target as HTMLElement | null;
                if (!target) return;
                if (target.closest('button, a, .ant-table-row-expand-icon, .ant-table-expand-icon')) return;
                navigate(`/risks/cards/${record.id}`);
              },
              className: s['root__clickable-row'],
            })}
          />
        </Spin>
        <Text type="secondary" className={s['root__table-note']}>
          В — вероятность, Т — тяжесть последствий (методика 5×5). Наведите на опасность для обоснования.
        </Text>
      </Card>

      <Modal
        title="Новая карта риска"
        open={cardModal}
        onCancel={() => setCardModal(false)}
        onOk={submitCard}
        okText="Сохранить"
        confirmLoading={addCard.isPending}
        destroyOnClose
      >
        <Form form={cardForm} layout="vertical" initialValues={{ probability: 3, severity: 3 }}>
          <Form.Item name="workplace" label="Рабочее место" rules={[{ required: true }]}>
            <Input placeholder="Напр. РМ-001 Сварщик" />
          </Form.Item>
          <Form.Item name="hazard" label="Опасность" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="probability" label="Вероятность (1–5)" rules={[{ required: true }]}>
            <Select options={probabilitySeverityOptions} />
          </Form.Item>
          <Form.Item name="severity" label="Тяжесть (1–5)" rules={[{ required: true }]}>
            <Select options={probabilitySeverityOptions} />
          </Form.Item>
          {previewScore !== null && (
            <Text className={s['root__score-preview']}>
              Балл: {previewScore} ({riskLevelFromScore(previewScore)})
            </Text>
          )}
          <Divider />
          <Form.Item name="justification" label="Обоснование оценки" extra="Обязательно для утверждения карты">
            <Input.TextArea rows={2} placeholder="Описание условий, статистика, факторы…" />
          </Form.Item>
          <Form.Item name="source" label="Источник данных" extra="Ссылка на НПА, акт, протокол измерений">
            <Input placeholder="Напр. ГОСТ 12.3.009-76" />
          </Form.Item>
          <Form.Item name="reviewDate" label="Дата пересмотра">
            <Input placeholder="ДД.ММ.ГГГГ" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingCard ? `Редактирование: ${editingCard.workplace} — ${editingCard.hazard}` : 'Редактирование'}
        open={editModal}
        onCancel={() => {
          setEditModal(false);
          setEditingCard(null);
        }}
        onOk={submitEdit}
        okText="Сохранить"
        confirmLoading={updateCard.isPending}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="probability" label="Вероятность (1–5)" rules={[{ required: true }]}>
            <Select options={probabilitySeverityOptions} />
          </Form.Item>
          <Form.Item name="severity" label="Тяжесть (1–5)" rules={[{ required: true }]}>
            <Select options={probabilitySeverityOptions} />
          </Form.Item>
          {editPreviewScore !== null && (
            <Text className={s['root__score-preview']}>
              Балл: {editPreviewScore} ({riskLevelFromScore(editPreviewScore)})
            </Text>
          )}
          <Divider />
          <Form.Item name="justification" label="Обоснование оценки" extra="Обязательно для утверждения">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="source" label="Источник данных">
            <Input />
          </Form.Item>
          <Form.Item name="reviewDate" label="Дата пересмотра">
            <Input placeholder="ДД.ММ.ГГГГ" />
          </Form.Item>
          <Form.Item name="status" label="Статус">
            <Select options={riskCardStatusOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
